import { Locale } from '@/common'
import Trans from '@/data/trans'
import { Range } from '@/data/types'
import { checkCondition } from '@/logic/mods'
import Select, { SelectOption } from '@/ui/select'
import { replaceReact } from '@/utils/format'
import React from 'react'
import HoverNumber from '../editor/HoverNumber'
import { PassiveSelector } from './passives'

export type StatsChanger = Record<string, number> | ((prev: Record<string, number>) => Record<string, number>)

export function TextLineSelect({
  domain,
  id,
  stats,
  onChange,
  expanded
}: {
  domain: string
  id: number
  stats: Record<string, number>
  onChange: (value: StatsChanger) => void
  expanded?: boolean
}) {
  const stat = Trans[domain][id]
  const values = stat.ids.map((id) => stats[id] || 0)
  const rule = stat.choose(values) ?? stat[Locale][0]
  const getOptions = React.useCallback(() => stat[Locale].map(({ string }, id) => ({ id, name: string })), [stat])
  const onSelect = React.useCallback(
    (opt?: SelectOption<number>) =>
      onChange((stats) => {
        const rule = opt && stat[Locale][opt.id]
        if (!rule) return stats
        stats = { ...stats }
        stat.ids.forEach((id, index) => {
          stats[id] = rule.condition[index].max ?? 0
        })
        return stats
      }),
    [onChange, stat]
  )
  if (expanded) return rule.string
  return (
    <Select
      options={getOptions}
      value={rule && { id: stat[Locale].indexOf(rule), name: rule.string }}
      onChange={onSelect}
      className="ui-Select poe-inner-select"
    />
  )
}

export function TextLine({
  domain,
  id,
  stats,
  limits,
  onChange,
  disabled,
  index
}: {
  domain: string
  id: number
  stats: Record<string, number>
  onChange: (id: string, value: number) => void
  limits: Range[]
  disabled?: boolean
  index?: number
}) {
  const stat = Trans.all[id]
  const rule = index != null ? stat[Locale][index] : stat.choose(limits)
  const valid = stat[Locale].filter((line) =>
    line.condition.every((cond, index) => checkCondition(cond, limits[index] || 0, false))
  )
  if (index == null && valid.length > 1 && rule?.format.every((s) => s === 'ignore')) {
    const changer = (stats: StatsChanger) => {
      // We have to do this weird stuff and hope that only one stat is actually changed
      if (typeof stats === 'function') stats = stats({})
      for (const [id, value] of Object.entries(stats)) {
        onChange(id, value)
      }
    }
    return <TextLineSelect domain={domain} id={id} stats={stats} onChange={changer} expanded={disabled} />
  }
  if (!rule) return null

  return (
    <div>
      {replaceReact(rule.string, /{(\d+)}/g, (m, index) => {
        index = parseInt(index)
        const format = rule.format[index]
        const handler = rule.index_handlers[index]
        const statId = stat.ids[index]
        if (handler.type) {
          return (
            <PassiveSelector
              value={stats[statId] ?? 0}
              onChange={(value) => onChange(statId, value)}
              range={limits[index]}
            />
          )
        }
        const onSet = (value: number) => onChange(statId, Math.round(handler.reverse(value)))
        const range = handler.apply(limits[index]) as Range
        const value = handler.apply(stats[statId] ?? 0) as number
        return (
          <>
            {format.includes('+') && range.max > 0 && '+'}
            {range.min === range.max && value === range.min ? (
              <span>{parseFloat(value.toFixed(2))}</span>
            ) : (
              <HoverNumber {...range} step={handler.step} value={value} onChange={onSet} disabled={disabled} />
            )}
            {!!format.includes('%') && '%'}
          </>
        )
      })}
    </div>
  )
}
